/**
 * Shared column-sort helpers for server-ordered tables.
 *
 * Platform three-state cycle (every sortable column):
 *   unsorted → descending → ascending → unsorted (cleared)
 */

export type TableSortDirection = 'asc' | 'desc' | '';

export type ColumnSortState = {
    sortColumn: string;
    sortDirection: TableSortDirection;
};

export const EMPTY_SORT_STATE: ColumnSortState = {
    sortColumn: '',
    sortDirection: '',
};

export function toggleColumnSort(state: ColumnSortState, column: string): ColumnSortState {
    if (state.sortColumn !== column || !state.sortDirection) {
        return {sortColumn: column, sortDirection: 'desc'};
    }
    if (state.sortDirection === 'desc') {
        return {sortColumn: column, sortDirection: 'asc'};
    }
    // Ascending → clear back to backend default ordering
    return {...EMPTY_SORT_STATE};
}

/** Encode for API body `sort_column` or query `ordering` (`field` or `-field`). */
export function encodeSortColumnParam(state: ColumnSortState): string {
    if (!state.sortColumn || !state.sortDirection) {
        return '';
    }
    return state.sortDirection === 'desc' ? `-${state.sortColumn}` : state.sortColumn;
}

export function sortStateFromOrdering(ordering: string): ColumnSortState {
    if (!ordering) {
        return {...EMPTY_SORT_STATE};
    }
    if (ordering.startsWith('-')) {
        return {sortColumn: ordering.slice(1), sortDirection: 'desc'};
    }
    return {sortColumn: ordering, sortDirection: 'asc'};
}

/**
 * Toggle for DRF `ordering` strings.
 * Returns '' when cleared so callers fall back to backend defaults.
 */
export function toggleOrdering(field: string, currentOrdering: string): string {
    const current = sortStateFromOrdering(currentOrdering || '');
    return encodeSortColumnParam(toggleColumnSort(current, field));
}

export function sortDirectionForField(
    field: string,
    ordering: string,
): Exclude<TableSortDirection, ''> | null {
    const state = sortStateFromOrdering(ordering || '');
    if (!state.sortColumn || state.sortColumn !== field || !state.sortDirection) {
        return null;
    }
    return state.sortDirection;
}

export function isSortActive(
    state: ColumnSortState,
    column: string,
    direction: Exclude<TableSortDirection, ''>,
): boolean {
    return state.sortColumn === column && state.sortDirection === direction;
}
